// The electrical schematic's views on a sheet (docs/claude/elecSchematic.md): what
// is picked inside a view, its drags and its wire tool, its menus and its panels.
// The model and its layouts are elecSchematic.ts / elecLayout.ts; this is the UI.
import {
  defaultName,
  dictDefaults,
  NAME_LANGS,
  productName,
} from '../../shared/names';
import {
  type ElecCellId,
  type ElecRowId,
  type ElecView,
  type SchematicLineStyle,
  type Sheet,
  type TextI18n,
  type Vec2,
} from '../../shared/types';
import { formatCableLength } from '../cableLength';
import { type CtxItem } from './contextMenus';
import {
  commitGesture,
  drawingRev,
  invalidate,
  pushHistory,
  renderProperties,
  state,
} from './core';
import {
  openDialog,
  showPrompt,
} from '../dialog';
import {
  elecBoxUnder,
  type ElecDrag,
  elecDragDrop,
  elecDragLive,
  elecDragMove,
  elecDragShows,
  elecDragStart,
  elecDropTarget,
  elecFacing,
} from '../elecDrag';
import {
  drawElecView,
  ELEC_AUTO_MARK_PX,
  ELEC_FONT,
  type ElecDrawState,
  elecHandBackColumns,
  elecSchematicNow,
  elecSettleViewColumns,
  elecSplitLists,
} from '../elecDraw';
import {
  type ElecEdit,
  type ElecWireFigures,
} from '../elecEdits';
import {
  ELEC_AUTO_MARK,
  ELEC_BOX_PAD,
  ELEC_TEXT_MM,
  elecAutoMarkAt,
  type ElecHit,
  elecHitTest,
  type ElecLayout,
  type ElecPick,
  elecPickOf,
  elecPickShape,
  elecSplitWidth,
  elecTableCellAt,
  elecTableCellRect,
  splitElecColumns,
} from '../elecLayout';
import {
  ELEC_NEW_COLUMN,
  ELEC_STENAL,
  type ElecColumn,
  type ElecModel,
  elecNum,
  elecPlantRoomWord,
  type ElecWire,
  parseElecCell,
} from '../elecSchematic';
import {
  getUiLang,
  tr,
  trIn,
} from '../i18n';
import { nameDefaultsRows } from './nameFields';
import { numberedNames } from '../naming';
import {
  buttonRow,
  checkboxRow,
  colorRow,
  deleteRow,
  infoRow,
  nameRow,
  numberRow,
  parseMeasure,
  sectionTitle,
  selectRow,
  textFieldRow,
} from './panelRows';
import {
  type SchemSel,
  schHitObject,
} from '../schematic';
import { elecShiftView } from '../sheetObjects';
import {
  formatPower,
  parsePower,
} from '../variants';
import { sheetDisplayToggle } from './sheetPanel';
import { renderViewTabs } from './sheetStrip';
import {
  copiedSheetName,
  makeSheet,
  paperToScreen,
  type PaperView,
  screenToPaper,
} from '../sheets';
import { type EditorLook } from '../inlineText';
import {
  canvas,
  cellEditorUi,
  flashStatus,
  flashToolStatus,
  screenPos,
  type UndoMark,
  undoSteps,
} from '../main';
import {
  activeSheet,
  drawingSheets,
  endPaperOneShot,
  paperView,
  schemSel,
  schemSelHas,
  schemSelSet,
  schTool,
  setSchemSel,
  setSchTool,
  sheetTableAt,
  sheetWhiteArea,
  toggleSchemSel,
} from './sheetSpace';
import {
  beginSchMultiMove,
  deleteSchematicSelected,
  LINE_STYLES,
  lineStyleRow,
  schBadgeAt,
  schCloseBoxEditor,
  schEditBoxId,
  schHandleAt,
  schUid,
  schVertexAt,
} from './sheetSchematic';

/** This module's state that main.ts reads and writes too. */
export const elecSheet = {
  /** A wire being drawn by hand (the Add wire tool, `schTool` 'elecWire'): its first
   *  box once clicked, and the view that box is in. */
  elecWireDraft: null as { viewId?: string; from?: string; cur?: Vec2 } | null,
  /** What is picked inside the selected view (a box, a wire, a cell, a border, a
   *  header, its table or legend); null = the view as a whole. */
  elecSub: null as ElecPick | null,
  /** A press on a view being dragged (elecDrag.ts), the view it is on, and — a
   *  live drag once it moved — where its step began. */
  elecDrag: null as { viewId: string; d: ElecDrag; start?: UndoMark } | null,
};

// ---- The electrical schematic's views on a sheet (docs/claude/elecSchematic.md) ----

/** A schematic column's default names (names.md): a room's plan label in each
 *  language, or a hand column's "New column". */
function elecColumnDefaults(c: ElecColumn): TextI18n {
  if (!c.roomId) return dictDefaults(ELEC_NEW_COLUMN, trIn);
  const out: TextI18n = {};
  for (const l of NAME_LANGS) out[l] = numberedNames(state.drawing.instances, (i) => productName(i, l)).get(c.roomId) ?? '';
  return out;
}
/** The schematic in use (elecDraw.ts): its model, its views' layouts, its edits. */
const elecNow = elecSchematicNow({ drawing: () => state.drawing, revision: () => drawingRev() });
export function elecModelNow(): ElecModel { return elecNow.model(); }
export function elecLayoutNow(view: ElecView): ElecLayout { return elecNow.layout(view); }
/** A schematic edit as one undo step (`push` false: inside a gesture, or a row
 *  that records its own), then the panel again (unless `panel` is false) and
 *  the canvas. Returns the key of what it added. */
function elecEdit(edit: ElecEdit, opts: { push?: boolean; panel?: boolean } = {}): string | null {
  if (elecNow.refusal(edit) === 'loop') { flashToolStatus(tr('This feed would run in a loop: a box cannot be fed from what it feeds.')); if (opts.panel !== false) renderProperties(); return null; }
  if (opts.push !== false) pushHistory();
  const made = elecNow.edit(edit);
  if (opts.panel !== false) renderProperties();
  invalidate();
  return made;
}
export function elecViewById(sh: Sheet, id: string): ElecView | undefined { return sh.elecViews?.find((v) => v.id === id); }
/** Views about to go (Delete, Cut, their sheet deleted), in the delete's step:
 *  the columns only they showed are handed back to the others. */
export function elecViewsGo(views: readonly ElecView[]): void {
  if (views.length) elecHandBackColumns(elecModelNow(), state.drawing, views);
}
/** The selected view of the active sheet (the lone selection), if any. */
function selectedElecView(): { sh: Sheet; view: ElecView } | null {
  const sh = activeSheet();
  if (!sh || schemSel?.kind !== 'elec' || schemSelSet.length !== 1) return null;
  const view = elecViewById(sh, schemSel.id);
  return view ? { sh, view } : null;
}

/** `elecSub` let go when what it picks is not in its view any more — an undo,
 *  a column removed, the table hidden: a stale pick's card, its Delete and its
 *  menu acted on what is gone (a box added to a removed column's cell, a hidden
 *  table moved onto the grid). Run before any of them reads it. */
function elecDropStalePick(): void {
  if (!elecSheet.elecSub) return;
  const s = selectedElecView();
  if (!s || !elecPickShape(elecLayoutNow(s.view), elecSheet.elecSub)) elecSheet.elecSub = null;
}

/** The topmost view under a paper point, and what of it is there; none under a
 *  table, which is drawn over the views (a press there once moved the view). */
export function elecViewAt(sh: Sheet, paper: Vec2, pv: PaperView): { view: ElecView; layout: ElecLayout; hit: ElecHit } | null {
  if (sheetTableAt(sh, pv, paperToScreen(pv, paper.x, paper.y))) return null; // a table is drawn over it
  const list = elecNow.sheet(sh);
  const tol = Math.min(1.2, Math.max(0.3, 3 / pv.scale));
  for (let i = list.length - 1; i >= 0; i--) {
    const hit = elecHitTest(list[i].layout, paper, tol);
    if (hit) return { ...list[i], hit };
  }
  return null;
}

/** Insert ▸ Electrical schematic: a view of every column at the top left of the
 *  page's white area, selected (decision 13). One undo step. */
export function insertElecView(): void {
  const sh = activeSheet();
  if (!sh) { flashToolStatus(tr('Open a sheet to place the electrical schematic on it.')); return; }
  pushHistory();
  const area = sheetWhiteArea(sh);
  const view: ElecView = { id: schUid('elec'), x: Math.round(area.x0), y: Math.round(area.y0) };
  (sh.elecViews ??= []).push(view);
  setSchTool('select');
  setSchemSel({ kind: 'elec', id: view.id });
  const b = elecLayoutNow(view).bounds;
  if (b.x + b.w > area.x1 + 0.5 || b.y + b.h > area.y1 + 0.5) flashToolStatus(tr('The schematic is bigger than the page: Split to a new sheet puts the columns that do not fit on another.'));
  renderProperties();
  invalidate();
}

/** Draw a sheet's views: on screen with the selection (`live`), in print without. */
export function drawSheetElecViews(c: CanvasRenderingContext2D, pv: PaperView, sh: Sheet, ink: string, live: boolean): void {
  for (const { view, layout } of elecNow.sheet(sh)) {
    if (!live) { drawElecView(c, pv, layout, ink); continue; }
    const primary = schemSel?.kind === 'elec' && schemSel.id === view.id;
    const shows = elecSheet.elecDrag?.viewId === view.id ? elecDragShows(elecSheet.elecDrag.d, layout, elecModelNow(), (e) => elecNow.refusal(e)) : {};
    drawElecView(c, pv, layout, ink, {
      selected: schemSelSet.some((s) => s.kind === 'elec' && s.id === view.id),
      sub: primary ? elecSheet.elecSub : null,
      drag: shows.drag ?? null,
      wireFrom: elecSheet.elecWireDraft?.viewId === view.id ? elecSheet.elecWireDraft.from : null,
      rubber: shows.rubber ?? elecWireRubber(view, layout),
      autoMarks: true,
      autoMarkPx: ELEC_AUTO_MARK_PX,
    });
  }
}

/** Add wire's wire on its way in a view, from its first box to the cursor — or
 *  to the box it would land on. */
function elecWireRubber(view: ElecView, layout: ElecLayout): ElecDrawState['rubber'] {
  const a = elecSheet.elecWireDraft;
  if (schTool !== 'elecWire' || a?.viewId !== view.id || !a.from || !a.cur) return null;
  const fb = layout.boxes.find((b) => b.key === a.from);
  if (!fb) return null;
  const tb = elecBoxUnder(layout, a.cur), ok = tb && tb.key !== a.from;
  const toward = ok ? { x: tb.rect.x + tb.rect.w / 2, y: tb.rect.y + tb.rect.h / 2 } : a.cur;
  const from = elecFacing(fb.rect, toward);
  return { from, to: ok ? elecFacing(tb.rect, from) : a.cur, color: ELEC_STENAL, target: ok ? tb.key : null };
}

/** A press on a view (Select tool): picks the view and what is under the cursor,
 *  and starts what a drag of that does — a box moves to another slot or cell, a
 *  jog or a border slides, the table or legend moves, anything else moves the
 *  whole view. False when no view is there, or a sheet object is over it. */
export function elecMouseDown(e: MouseEvent, sh: Sheet, pv: PaperView): boolean {
  if (e.button !== 0) return false;
  const sp = screenPos(e), paper = screenToPaper(pv, sp);
  if (schTool === 'elecWire') return elecWireClick(sh, paper, pv);
  if (schTool === 'elecBox') return elecBoxClick(sh, paper, pv);
  if (schTool !== 'select') return false;
  if (schHandleAt(sh, pv, sp) || schVertexAt(sh, pv, sp) || schBadgeAt(sh, pv, sp) || schHitObject(sh, paper, 3 / pv.scale)) return false;
  const at = elecViewAt(sh, paper, pv);
  if (!at) return false;
  if (schEditBoxId) schCloseBoxEditor(true);
  const sel: SchemSel = { kind: 'elec', id: at.view.id };
  if (e.shiftKey || e.ctrlKey || e.metaKey) { toggleSchemSel(sel); renderProperties(); invalidate(); return true; }
  if (schemSelSet.length > 1 && schemSelHas(sel)) { beginSchMultiMove(sh, paper); return true; }
  setSchemSel(sel);
  elecSheet.elecSub = elecPickOf(at.hit);
  elecSheet.elecDrag = { viewId: at.view.id, d: elecDragStart(at.view, at.layout, elecModelNow(), at.hit, paper) };
  renderProperties();
  invalidate();
  return true;
}

/** A drag on a view let go of and, once it moved, put back as the press found
 *  it: Esc, or Ctrl+Z — the step undo takes, as a grip's. False when there was
 *  nothing to put back (a press that had not moved). */
export function cancelElecDrag(): boolean {
  const w = elecSheet.elecDrag;
  if (!w) return false;
  elecSheet.elecDrag = null;
  if (!w.d.moved) return false;
  if (w.start) undoSteps.end('cancel', w.start);
  renderProperties();
  invalidate();
  return true;
}

/** A drawing restored or loaded (an undo, a redo, a draft or file opened): Add
 *  wire's first box, a drag on a view and a pick in one are let go of — each
 *  named what the drawing before had (a wire was stored to a box undone). */
export function elecLetGo(): void {
  if (elecSheet.elecWireDraft) elecSheet.elecWireDraft = {};
  elecSheet.elecDrag = null;
  elecDropStalePick();
}

/** Continue a drag on a view. True while one is under way. */
export function elecMouseMove(e: MouseEvent, sh: Sheet, pv: PaperView): boolean {
  const w = elecSheet.elecDrag;
  if (!w) return false;
  const view = elecViewById(sh, w.viewId);
  if (!view) { elecSheet.elecDrag = null; return false; }
  const step = elecDragMove(w.d, view, screenToPaper(pv, screenPos(e)), 3 / pv.scale, elecLayoutNow(view));
  if (!step) return true;
  // A live drag records its step when it first moves: a bare click changes nothing.
  if (step.begin) w.start = undoSteps.begin();
  if (step.edit) elecNow.edit(step.edit);
  if (step.shift && w.d.mode === 'view') { Object.assign(view, structuredClone(w.d.v0)); elecShiftView(view, step.shift.x, step.shift.y); }
  invalidate();
  return true;
}

/** End a drag on a view: a live one commits the gesture it began; a box or a
 *  wire's end dropped makes its edit, one undo step — or says why it cannot. */
export function elecMouseUp(sh: Sheet): boolean {
  const w = elecSheet.elecDrag;
  if (!w) return false;
  elecSheet.elecDrag = null;
  const view = elecViewById(sh, w.viewId);
  if (elecDragLive(w.d)) { if (w.d.moved) commitGesture(); }
  else if (view) {
    const drop = elecDragDrop(w.d, elecLayoutNow(view), elecModelNow());
    const why = drop && elecNow.refusal(drop.edit);
    if (why === 'part') flashToolStatus(tr('A project wire joins two parts: drop its end on a part’s box.'));
    else if (why === 'fixed') flashToolStatus(tr('This end stays at its light or its client box: drag the wire’s other end.'));
    else if (why === 'loop') flashToolStatus(tr('This feed would run in a loop: a box cannot be fed from what it feeds.'));
    else if (drop && !why) { elecEdit(drop.edit, { panel: false }); elecSheet.elecSub = drop.pick; }
  }
  renderProperties();
  invalidate();
  return true;
}

/** With Add wire armed and its first box clicked, the wire follows the cursor. */
export function elecWireHover(pv: PaperView, pos: Vec2): void {
  const d = elecSheet.elecWireDraft;
  if (schTool !== 'elecWire' || !d?.from) return;
  d.cur = screenToPaper(pv, pos);
  invalidate();
}

/** Over the triangle of a box the schematic made itself, the canvas's tooltip
 *  says "auto". */
let elecAutoTipOn = false;
export function elecAutoTip(sh: Sheet, pv: PaperView, pos: Vec2): void {
  const paper = screenToPaper(pv, pos);
  const on = elecNow.sheet(sh).some(({ layout }) => !!elecAutoMarkAt(layout, paper, Math.max(ELEC_AUTO_MARK, ELEC_AUTO_MARK_PX / pv.scale)));
  if (on === elecAutoTipOn) return;
  elecAutoTipOn = on;
  if (on) canvas.title = tr('auto'); else canvas.removeAttribute('title');
}

/** Add wire / Add box (the sheet's Electrical toolbar, Insert ▸ On a sheet):
 *  armed on a sheet with a schematic on it; its button again disarms it. */
export function armElecTool(t: 'elecBox' | 'elecWire'): void {
  const sh = activeSheet();
  if (!sh) { flashToolStatus(tr('Open a sheet to place the electrical schematic on it.')); return; }
  if (schTool === t) { setSchTool('select'); return; }
  if (!sh.elecViews?.length) { setSchTool('select'); flashToolStatus(tr('Place the electrical schematic on this sheet first (Electrical schematic).')); return; }
  setSchTool(t);
}

/** A click with Add wire: the first box, then the second, which makes the wire,
 *  numbered S… (decision 18), picked; then Select again. A click off the
 *  schematic (or off the view of its first box) ends the tool and goes on. */
function elecWireClick(sh: Sheet, paper: Vec2, pv: PaperView): boolean {
  const d = (elecSheet.elecWireDraft ??= {});
  const at = elecViewAt(sh, paper, pv);
  if (!at || (d.viewId && at.view.id !== d.viewId)) { setSchTool('select'); return false; }
  // A wire's end dot is over its box's edge: it picks that box.
  const box = at.hit.kind === 'box' ? at.hit.key : at.hit.kind === 'end' ? at.hit.box : null;
  if (!box) { flashToolStatus(tr('Click a box: the wire runs from the first box clicked to the second.')); return true; }
  if (!d.from) {
    d.viewId = at.view.id; d.from = box; d.cur = paper;
    setSchemSel({ kind: 'elec', id: at.view.id });
    renderProperties(); invalidate();
    return true;
  }
  if (d.from === box) return true;
  const key = elecEdit({ kind: 'wireAdd', id: schUid('sw'), from: d.from, to: box }, { panel: false });
  endPaperOneShot();
  setSchemSel({ kind: 'elec', id: at.view.id });
  if (key) elecSheet.elecSub = { kind: 'wire', key };
  renderProperties();
  return true;
}

/** A click with Add box: a box added by hand to the cell under it, among its
 *  boxes where the click is (decision 15), picked; then Select again. */
function elecBoxClick(sh: Sheet, paper: Vec2, pv: PaperView): boolean {
  const at = elecViewAt(sh, paper, pv);
  if (!at) { setSchTool('select'); return false; }
  const t = elecDropTarget(at.layout, elecModelNow(), '', paper);
  if (!t) { flashToolStatus(tr('Click a cell of the schematic: the box goes there.')); return true; }
  endPaperOneShot();
  setSchemSel({ kind: 'elec', id: at.view.id });
  elecAddHandBox(t.cell, t.index, t.drawn, t.auto);
  return true;
}

/** Double-click on a view: a box's text is replaced (decision 12), a wire's
 *  figures edited, a cell of the cable list edited in place — with Select only: a tool's two clicks were its own (Add
 *  wire's first box twice), and nothing opens over them. */
export function elecDblClick(sh: Sheet, paper: Vec2): boolean {
  const at = elecViewAt(sh, paper, paperView());
  if (!at) return false;
  if (schTool !== 'select') return true;
  setSchemSel({ kind: 'elec', id: at.view.id });
  const p = (elecSheet.elecSub = elecPickOf(at.hit));
  renderProperties();
  invalidate();
  elecPickRow(p).open?.(p, paper);
  return true;
}

/** Double-click on the cable list: a Cable or a Length cell edited in place
 *  (the one inline editor, over its cell), a No. its wire's dialog. Each its
 *  wire's own figure (the `wire` edit: a project wire's on its connection), one
 *  undo step, none when nothing changed. The length in metres as the list shows
 *  it (a bare number is metres, a unit typed wins); empty, worked out again. */
function elecOpenTableCell(paper: Vec2): void {
  const s = selectedElecView(), t = s && elecLayoutNow(s.view).table, cell = t && elecTableCellAt(t, paper);
  const model = elecModelNow(), w = cell && model.wires.find((x) => x.key === cell.key);
  if (!s || !cell || !w) return;
  if (cell.col === 'number') { void elecEditWire(w.key); return; }
  const { view } = s, { key, col } = cell;
  const start = col === 'cable' ? w.cable : w.length != null ? `${elecNum(w.length / 1000, model.lang)} m` : '';
  let look: EditorLook = {};
  cellEditorUi.open({
    text: start,
    // The cell itself: its rectangle, its typeface and size, its text where
    // the list draws it (one line, scrolled, never wrapped); where the cell is
    // gone for a frame, where it was.
    look: () => {
      const tt = elecLayoutNow(view).table, r = tt && elecTableCellRect(tt, key, col);
      if (r) {
        const pv = paperView(), a = paperToScreen(pv, r.x, r.y), k = pv.scale, border = 1;
        look = {
          left: `${a.x}px`, top: `${a.y}px`, width: `${r.w * k}px`, height: `${r.h * k}px`,
          fontFamily: ELEC_FONT, fontSize: `${ELEC_TEXT_MM * k}px`, lineHeight: `${r.h * k - 2 * border}px`,
          padding: `0 ${Math.max(0, ELEC_BOX_PAD * k - border)}px`, border: `${border}px solid var(--accent)`,
          borderRadius: '0', whiteSpace: 'pre',
        };
      }
      return look;
    },
    keys: { enter: 'commits', esc: 'cancels' },
    gesture: false,
    caret: 'all',
    commit: (text) => {
      const v = text.trim();
      if (v === start.trim()) return;
      if (col === 'cable') { elecEdit({ kind: 'wire', key, figures: { cable: v } }); return; }
      const m = v ? parseMeasure(v, 'm') : null;
      if (v && (m == null || !Number.isFinite(m) || m < 0)) { flashToolStatus(tr('Not a length: type it in metres, or with its unit (12 m, 1250 cm).')); return; }
      const mm = m == null ? null : Math.round(m * 1000);
      if (mm === (w.lengthTyped == null ? null : Math.round(w.lengthTyped))) return;
      elecEdit({ kind: 'wire', key, figures: { length: mm } });
    },
    closed: () => { renderProperties(); invalidate(); },
  });
  invalidate();
}

/** Replace a box's text in a dialog (empty: back to its own text). */
async function elecEditBoxText(key: string): Promise<void> {
  const box = elecModelNow().boxByKey.get(key);
  if (!box) return;
  const own = box.ownLines.join('\n');
  const v = await showPrompt('Box text', 'Text (empty: automatic)', own, 'Save', true);
  if (v == null || v === own) return;
  elecEdit({ kind: 'boxText', key, text: v });
}
/** A wire's figures, all in one dialog (double-click it, or Edit wire… on its
 *  card or menu), saved as one undo step — only what was changed. A project
 *  wire's are its connection's: who lays it, its cable in the drawing's
 *  language, its length (empty: worked out from the route), its parallel lines.
 *  A hand wire's are its own, its two ends too. A light's or a preparation's are
 *  typed over the automatic ones (`ElecWireEdit`), a light's feed too. Every
 *  wire: its parallel cables (one wire each, the same path), straight or at
 *  right angles. */
async function elecEditWire(key: string): Promise<void> {
  const model = elecModelNow();
  const w = model.wires.find((x) => x.key === key);
  if (!w) return;
  const boxName = (k: string): string => { const b = model.boxByKey.get(k); return b ? `${b.lines[0] ?? k} · ${elecCellName(model, b.cell)}` : k; };
  const body = document.createElement('div');
  body.className = 'elec-wire-form';
  const field = <T extends HTMLElement>(label: string, el: T): T => {
    const row = document.createElement('div'); row.className = 'form-row';
    const lab = document.createElement('label'); lab.textContent = tr(label);
    row.append(lab, el); body.appendChild(row);
    return el;
  };
  const info = (label: string, text: string): void => { const el = document.createElement('div'); el.textContent = text; field(label, el); };
  const select = (label: string, role: string, options: ReadonlyArray<readonly [string, string]>, value: string): HTMLSelectElement => {
    const el = document.createElement('select'); el.dataset.role = role;
    for (const [v, t] of options) { const o = document.createElement('option'); o.value = v; o.textContent = t; el.appendChild(o); }
    el.value = value;
    return field(label, el);
  };
  const input = (label: string, role: string, value: string, placeholder = ''): HTMLInputElement => {
    const el = document.createElement('input'); el.type = 'text'; el.dataset.role = role; el.value = value; el.placeholder = placeholder;
    return field(label, el);
  };

  info('No.', w.number);
  const boxes = model.boxes.map((b) => [b.key, boxName(b.key)] as const);
  let from: HTMLSelectElement | null = null, to: HTMLSelectElement | null = null;
  if (w.kind === 'hand') { from = select('From', 'elec-wire-from', boxes, w.from); to = select('To', 'elec-wire-to', boxes, w.to); } else { info('From', boxName(w.from)); info('To', boxName(w.to)); }
  const by = select('Installed by', 'elec-wire-by', [['stenal', tr('Stenal')], ['client', tr('Client')]], w.installedBy);
  const cable = input('Cable', 'elec-wire-cable', w.cable);
  // The length typed by hand, else the worked-out one as the placeholder.
  const typed = w.lengthTyped;
  const len = input('Length (mm)', 'elec-wire-length', typed != null ? String(Math.round(typed)) : '', w.lengthAuto != null ? String(Math.round(w.lengthAuto)) : '—');
  len.inputMode = 'decimal';
  len.title = tr('Empty: worked out from the route, where it can be.');
  // Its parallel cables, those the schematic leaves out (removed) too: a
  // project wire's are its connection's lines.
  const lineCount = Math.max(1, w.lines ?? 1);
  const lines = document.createElement('input'); lines.type = 'number'; lines.min = '1'; lines.step = '1'; lines.dataset.role = 'elec-wire-lines';
  lines.value = String(lineCount);
  field('Parallel lines', lines);
  const fed = w.kind === 'auto' ? select('Fed from', 'elec-wire-fed', elecFeedOptions(model, w).map(([v, t]) => [v, v ? t : tr(t)] as const), w.fed ?? '') : null;
  const fed0 = fed?.value;
  const straight = document.createElement('input'); straight.type = 'checkbox'; straight.checked = !!w.straight; straight.dataset.role = 'elec-wire-straight';
  straight.style.alignSelf = 'flex-start';
  field('Straight line', straight);
  // How it is drawn: its own over the schematic's, its colour over its installer's.
  const width = input('Line width (mm)', 'elec-wire-width', String(w.look.thickness));
  width.inputMode = 'decimal';
  const style = select('Style', 'elec-wire-style', LINE_STYLES.map(([v, t]) => [v, tr(t)] as const), w.look.style);
  const colour = document.createElement('input'); colour.type = 'color'; colour.value = w.look.color; colour.dataset.role = 'elec-wire-colour';
  colour.style.alignSelf = 'flex-start';
  field('Colour', colour);

  // Only what was changed, made after the one pushHistory.
  const figures = await new Promise<ElecWireFigures | null>((resolve) => {
    const save = (): void => {
      const raw = len.value.trim();
      const mm = raw ? parseMeasure(raw, 'mm') : null;
      if (raw && (mm == null || !Number.isFinite(mm) || mm < 0)) { flashStatus(tr('Not a length: type it in mm, or with a unit (e.g. 12 m).')); len.focus(); return; }
      if (from && to && from.value === to.value) { flashStatus(tr('A wire runs between two different boxes.')); to.focus(); return; }
      const wv = parseMeasure(width.value.trim(), 'mm');
      if (wv == null || !Number.isFinite(wv) || wv <= 0) { flashStatus(tr('Not a length: type it in mm, or with a unit (e.g. 12 m).')); width.focus(); return; }
      const f: ElecWireFigures = {};
      if (by.value !== w.installedBy) f.installedBy = by.value === 'client' ? 'client' : 'stenal';
      if (cable.value.trim() !== w.cable) f.cable = cable.value;
      const n = mm == null ? null : Math.round(mm);
      if (n !== (typed == null ? null : Math.round(typed))) f.length = n;
      const k = Math.max(1, Math.round(Number(lines.value) || 1));
      if (k !== lineCount) f.lines = k;
      if (from && to && (from.value !== w.from || to.value !== w.to)) f.ends = { from: from.value, to: to.value };
      if (fed && fed.value !== fed0) f.fed = fed.value || null;
      if (straight.checked !== !!w.straight) f.straight = straight.checked;
      if (Math.abs(wv - w.look.thickness) > 1e-9) f.thickness = wv;
      if (style.value !== w.look.style) f.style = style.value as SchematicLineStyle;
      if (colour.value.toLowerCase() !== w.look.color.toLowerCase()) f.color = colour.value;
      d.close();
      resolve(f);
    };
    const d = openDialog({
      title: 'Schematic wire',
      body,
      buttons: [
        { label: 'Cancel', role: 'cancel', onClick: () => { d.close(); resolve(null); } },
        { label: 'Save', role: 'confirm', primary: true, onClick: save },
      ],
    });
    d.root.dataset.role = 'elec-wire-dialog';
    setTimeout(() => { cable.focus(); cable.select(); }, 0);
  });
  if (!figures || !Object.keys(figures).length) return;
  const made = elecEdit({ kind: 'wire', key: w.key, figures }, { panel: false });
  if (made) elecSheet.elecSub = { kind: 'wire', key: made };
  renderProperties();
}

/** Delete what is picked in the selected view, when it can go on its own (its
 *  row's `remove`). False: Delete takes the view. */
export function elecDeletePick(sh: Sheet): boolean {
  elecDropStalePick();
  const s = selectedElecView();
  if (!s || s.sh !== sh || !elecSheet.elecSub) return false;
  const edit = elecPickRow(elecSheet.elecSub).remove(elecAtOf(s.view), elecSheet.elecSub);
  if (!edit) return false;
  elecEdit(edit, { panel: false });
  elecSheet.elecSub = null;
  renderProperties();
  return true;
}

/** A view's table or legend shown or hidden, where it is. */
function elecAsideShown(view: ElecView, which: 'table' | 'legend', shown: boolean): ElecEdit {
  if (shown) return { kind: 'viewAside', view, which, hidden: false };
  // Hidden already: it stays where it was (none is laid out to read it from).
  if (view[which]?.hidden) return { kind: 'viewAside', view, which, hidden: true };
  const r = (which === 'table' ? elecLayoutNow(view).table : elecLayoutNow(view).legend)?.rect;
  return { kind: 'viewAside', view, which, hidden: true, at: { x: r?.x ?? view.x, y: r?.y ?? view.y } };
}

/** The right-click menu's own items for what is picked in a view. */
export function elecMenuItems(): CtxItem[] {
  elecDropStalePick();
  const s = selectedElecView();
  return s && elecSheet.elecSub ? elecPickRow(elecSheet.elecSub).menu(elecAtOf(s.view), elecSheet.elecSub) : [];
}

/** The view a pick is in, the schematic, and the view's layout. */
interface ElecAt { view: ElecView; model: ElecModel; layout: ElecLayout }
const elecAtOf = (view: ElecView): ElecAt => ({ view, model: elecModelNow(), layout: elecLayoutNow(view) });
/** What a kind of pick in a view does: its card, first in the view's panel;
 *  Delete (the edit that takes it on its own; null: Delete takes the view); its
 *  right-click items; a double-click on it (null: picking it is all). A new
 *  kind does not compile without its row. */
interface ElecPickRow<P extends ElecPick> {
  card(body: HTMLElement, at: ElecAt, p: P): void;
  remove(at: ElecAt, p: P): ElecEdit | null;
  menu(at: ElecAt, p: P): CtxItem[];
  open: ((p: P, paper: Vec2) => void) | null;
}
const ELEC_PICKS: { [K in ElecPick['kind']]: ElecPickRow<Extract<ElecPick, { kind: K }>> } = {
  border: {
    card: (body, at, p) => renderElecBorderPanel(body, at.model, p.row, p.after),
    remove: (_at, p) => ({ kind: 'border', row: p.row, after: p.after, removed: true }),
    menu: () => [],
    open: null,
  },
  box: {
    card: (body, at, p) => renderElecBoxPanel(body, at.model, at.layout, p.key),
    remove: (at, p) => (at.model.boxByKey.has(p.key) ? { kind: 'boxHidden', key: p.key, hidden: true } : null),
    menu: (at, p) => elecBoxMenu(at, p.key),
    open: (p) => { void elecEditBoxText(p.key); },
  },
  cell: {
    card: (body, at, p) => renderElecCellPanel(body, at.model, p.id),
    remove: () => null,
    menu: (_at, p) => [{ label: 'Add a box here', run: () => elecAddHandBox(p.id) }],
    open: null,
  },
  columnHeader: {
    card: (body, at, p) => renderElecColumnPanel(body, at.model, p.id),
    remove: () => null,
    menu: () => [],
    open: null,
  },
  legend: {
    card: (body, at) => renderElecAsidePanel(body, at.view, 'legend'),
    remove: (at) => elecAsideShown(at.view, 'legend', false),
    menu: () => [],
    open: null,
  },
  rowHeader: {
    card: (body, at, p) => renderElecRowPanel(body, at.model, at.layout, p.row),
    remove: () => null,
    menu: () => [],
    open: null,
  },
  table: {
    card: (body, at) => renderElecAsidePanel(body, at.view, 'table'),
    remove: (at) => elecAsideShown(at.view, 'table', false),
    menu: () => [],
    open: (_p, paper) => elecOpenTableCell(paper),
  },
  wire: {
    card: (body, at, p) => renderElecWirePanel(body, at.model, p.key),
    remove: (at, p) => (at.model.wires.some((x) => x.key === p.key) ? { kind: 'wireRemoved', key: p.key, removed: true } : null),
    menu: (at, p) => elecWireMenu(at, p.key),
    open: (p) => { void elecEditWire(p.key); },
  },
};
const elecPickRow = <P extends ElecPick>(p: P): ElecPickRow<P> => ELEC_PICKS[p.kind] as unknown as ElecPickRow<P>;

function elecBoxMenu(at: ElecAt, key: string): CtxItem[] {
  const box = at.model.boxByKey.get(key);
  if (!box) return [];
  const out: CtxItem[] = [
    { label: 'Edit text…', run: () => { void elecEditBoxText(key); } },
    { label: 'Rotate 90° clockwise', run: () => elecTurnBox(at.layout, key, 1) },
    { label: 'Rotate 90° anticlockwise', run: () => elecTurnBox(at.layout, key, -1) },
  ];
  if (box.edited) out.push({ label: 'Automatic text', run: () => elecEdit({ kind: 'boxText', key, text: '' }) });
  if (box.row === 'client') out.push({ label: box.feeds ? 'Stop listing the loads' : 'List the loads it feeds', run: () => elecEdit({ kind: 'boxListLoads', key, on: !box.feeds }) });
  return out;
}
function elecWireMenu(at: ElecAt, key: string): CtxItem[] {
  const w = at.model.wires.find((x) => x.key === key);
  if (!w) return [];
  return [
    { label: 'Edit wire…', run: () => { void elecEditWire(key); } },
    { label: w.straight ? 'Right angles' : 'Straight line', run: () => elecEdit({ kind: 'wire', key, figures: { straight: !w.straight } }) },
  ];
}

/** Turn a box a quarter turn clockwise (1) or anticlockwise (-1), from how its
 *  view lays it out: one undo step. */
function elecTurnBox(layout: ElecLayout, key: string, dir: 1 | -1): void {
  const box = layout.boxes.find((b) => b.key === key);
  if (box) elecEdit({ kind: 'boxTurn', box, dir });
}

/** A box added by hand to a cell, picked: after its boxes, or — `index` — before
 *  the one at that slot (the cell's boxes then keep their order by hand). */
function elecAddHandBox(cell: ElecCellId, index?: number, drawn?: string[], auto?: string[]): void {
  const key = elecEdit({ kind: 'boxAdd', id: schUid('hb'), cell, index, ...(drawn ? { drawn } : {}), ...(auto ? { auto } : {}) }, { panel: false });
  if (key) elecSheet.elecSub = { kind: 'box', key };
  renderProperties();
}

/** Split a view whose columns do not fit its page (decision 14): it keeps the
 *  first that fit, and each further group goes onto a new sheet after it. */
function splitElecView(sh: Sheet, view: ElecView): void {
  const model = elecModelNow(), layout = elecLayoutNow(view);
  const area = sheetWhiteArea(sh);
  const cols = elecNow.columns(view);
  const groups = splitElecColumns({ ...model, columns: model.columns.filter((c) => cols.includes(c.id)) }, elecSplitWidth(layout, view.x, area.x1), elecNow.options(view));
  if (groups.length < 2) { flashToolStatus(tr('The schematic fits this page: nothing to split.')); return; }
  pushHistory();
  elecSettleViewColumns(model, state.drawing);
  // Every view of the split shows its group and leaves out the rest.
  const lists = elecSplitLists(model, view, groups);
  Object.assign(view, lists[0]);
  const sheets = drawingSheets();
  let at = sheets.indexOf(sh);
  lists.slice(1).forEach((list) => {
    const ns = Object.assign(makeSheet(sh.paper === 'custom' ? 'A3' : sh.paper, sh.orientation), copiedSheetName(sh, sheets));
    if (sh.paper === 'custom') { ns.paper = 'custom'; ns.widthMm = sh.widthMm; ns.heightMm = sh.heightMm; }
    ns.cartiglioId = sh.cartiglioId;
    const a = sheetWhiteArea(ns);
    ns.elecViews = [{ id: schUid('elec'), x: Math.round(a.x0), y: Math.round(a.y0), ...list, ...(view.showNumbers === false ? { showNumbers: false } : {}) }];
    sheets.splice(++at, 0, ns);
  });
  flashToolStatus(tr('The schematic now runs over {n} sheets.').replace('{n}', String(groups.length)));
  renderViewTabs();
  renderProperties();
  invalidate();
}

/** The panel of a selected view: what is picked in it first, then the view and
 *  the schematic it shows — its columns, plant rooms, rows and what is hidden. */
export function renderElecViewPanel(body: HTMLElement, sh: Sheet, id: string): void {
  const view = elecViewById(sh, id);
  if (!view) return;
  const model = elecModelNow(), layout = elecLayoutNow(view);
  elecDropStalePick();
  if (elecSheet.elecSub) renderElecPickPanel(body, view, model, layout, elecSheet.elecSub);
  body.appendChild(sectionTitle('Electrical schematic'));
  const wireRow = document.createElement('div'); wireRow.style.margin = '6px 0';
  const wireBtn = document.createElement('button');
  wireBtn.className = schTool === 'elecWire' ? 'toggle active' : 'toggle';
  wireBtn.dataset.role = 'elec-wire';
  wireBtn.textContent = tr('Draw a wire');
  wireBtn.title = tr('Click two boxes of the schematic: between two parts, a connection of the drawing (on the plan and in 3D); else a wire of the schematic’s own, numbered S1, S2 …');
  wireBtn.addEventListener('click', () => armElecTool('elecWire'));
  wireRow.appendChild(wireBtn); body.appendChild(wireRow);
  // The toggles record their own step.
  const toggle = (edit: ElecEdit): void => { elecNow.edit(edit); };
  body.appendChild(sheetDisplayToggle('Wire numbers', view.showNumbers !== false, (v) => toggle({ kind: 'viewNumbers', view, shown: v })));
  // Every wire's width and dash, where its own sets none (the whole schematic's).
  body.appendChild(numberRow('Wire width (mm)', model.wireThickness, (v) => elecEdit({ kind: 'wireLook', thickness: v }, { panel: false }), { commit: true }));
  body.appendChild(lineStyleRow(model.wireStyle, (v) => elecEdit({ kind: 'wireLook', style: v }, { panel: false }), 'Wire style'));
  body.appendChild(sheetDisplayToggle('Wire table', !view.table?.hidden, (v) => toggle(elecAsideShown(view, 'table', v))));
  body.appendChild(sheetDisplayToggle('Legend', !view.legend?.hidden, (v) => toggle(elecAsideShown(view, 'legend', v))));
  renderElecColumnsSection(body, sh, view, model);
  renderElecPlantRoomsSection(body, model);
  renderElecRowsSection(body, model);
  renderElecHiddenSection(body, model);
  const hint = document.createElement('div'); hint.className = 'prop-empty'; hint.style.marginTop = '8px';
  hint.textContent = tr('Drag the schematic to move it · drag a box to another slot or cell · double-click a box to replace its text, a wire to edit it · drag a wire’s jog or a cell’s border · drag a wire’s end onto another box.');
  body.appendChild(hint);
  body.appendChild(deleteRow(() => deleteSchematicSelected(sh)));
}

/** The picked part's card (its row's). */
function renderElecPickPanel(body: HTMLElement, view: ElecView, model: ElecModel, layout: ElecLayout, p: ElecPick): void {
  elecPickRow(p).card(body, { view, model, layout }, p);
}

/** A cell's name: its column's or plant room's, and its row's. */
function elecCellName(model: ElecModel, cell: ElecCellId): string {
  const { row, key } = parseElecCell(cell);
  const rowName = model.rows.find((r) => r.id === row)?.name ?? row;
  const where = row === 'technical' ? model.plantRooms.find((p) => p.id === key)?.name : model.columns.find((c) => c.id === key)?.name;
  return where ? `${where} · ${rowName}` : rowName;
}

function renderElecBoxPanel(body: HTMLElement, model: ElecModel, layout: ElecLayout, key: string): void {
  const box = model.boxByKey.get(key);
  if (!box) return;
  body.appendChild(sectionTitle('Schematic box'));
  body.appendChild(infoRow('Cell', elecCellName(model, box.cell)));
  // A quarter turn either way, its text with it.
  const turn = document.createElement('div'); turn.className = 'prop-row';
  const tl = document.createElement('label'); tl.textContent = tr('Rotate');
  const turns = document.createElement('div'); turns.style.display = 'flex'; turns.style.gap = '6px';
  for (const [dir, glyph, title, role] of [[-1, '↺', 'Rotate 90° anticlockwise', 'elec-box-ccw'], [1, '↻', 'Rotate 90° clockwise', 'elec-box-cw']] as const) {
    const b = document.createElement('button');
    b.textContent = glyph; b.title = tr(title); b.dataset.role = role; b.style.flex = '1';
    b.addEventListener('click', () => elecTurnBox(layout, key, dir));
    turns.appendChild(b);
  }
  turn.append(tl, turns); body.appendChild(turn);
  const row = document.createElement('div'); row.className = 'prop-row';
  const lab = document.createElement('label'); lab.textContent = tr('Text');
  const ta = document.createElement('textarea'); ta.rows = Math.max(2, box.ownLines.length); ta.value = box.ownLines.join('\n'); ta.style.resize = 'vertical';
  ta.dataset.role = 'elec-box-text';
  ta.addEventListener('change', () => elecEdit({ kind: 'boxText', key, text: ta.value }));
  row.append(lab, ta); body.appendChild(row);
  if (box.edited) body.appendChild(buttonRow('Automatic text', () => elecEdit({ kind: 'boxText', key, text: '' })));
  if (box.row === 'client') {
    body.appendChild(checkboxRow('List the loads it feeds', !!box.feeds, (v) => elecEdit({ kind: 'boxListLoads', key, on: v })));
    if (box.feeds) body.appendChild(infoRow('Total', `${Math.round(box.feeds.total)} W`));
  }
  // A linear light's LED of a run of several, or that run as one box.
  const run = box.run;
  if (run) {
    body.appendChild(checkboxRow('One box for the whole run', run.whole, (v) => {
      const made = elecEdit({ kind: 'lightRun', key, whole: v }, { panel: false });
      if (made) elecSheet.elecSub = { kind: 'box', key: made };
      renderProperties();
    }));
  }
  body.appendChild(checkboxRow('Electrical load', box.load != null, (v) => elecEdit({ kind: 'boxElectricalLoad', key, on: v })));
  if (box.kind === 'hand' && box.load != null) body.appendChild(elecBoxPowerRow(key, state.drawing.elecSchematic?.handBoxes?.find((h) => h.id === box.handId)?.power));
  if (box.load != null && !box.feeds) body.appendChild(infoRow('Total', `${Math.round(box.load)} W`));
  // Moved as this view draws it: a client box against the view's own order.
  if (layout.boxes.find((b) => b.key === key)?.moved ?? box.moved) {
    body.appendChild(buttonRow('Back to its own place', () => elecEdit({ kind: 'boxHome', key, ...(box.autoCell === 'client' ? { auto: layout.clientAuto } : {}) })));
  }
  if (box.kind !== 'hand') body.appendChild(buttonRow('Hide box', () => { elecEdit({ kind: 'boxHidden', key, hidden: true }, { panel: false }); elecSheet.elecSub = null; renderProperties(); }));
}

/** A hand box's own power, typed as "1.5 kW", "750 W" or a bare number of
 *  watts (`parsePower`); empty, none. */
function elecBoxPowerRow(key: string, power: number | undefined): HTMLElement {
  const row = document.createElement('div'); row.className = 'prop-row';
  const lab = document.createElement('label'); lab.textContent = tr('Power');
  const el = document.createElement('input'); el.type = 'text'; el.inputMode = 'decimal';
  el.dataset.role = 'elec-box-power'; el.title = tr('Watts, or kW: 36 kW, 750 W, 36000');
  el.value = power ? formatPower(power) : '';
  el.addEventListener('change', () => {
    const typed = el.value.trim(), w = typed ? parsePower(typed) : null;
    if (typed && w == null) { el.value = power ? formatPower(power) : ''; return; }
    elecEdit({ kind: 'boxPower', key, w });
  });
  el.addEventListener('keydown', (e) => { if (e.key === 'Enter') el.blur(); });
  row.append(lab, el);
  return row;
}

function renderElecWirePanel(body: HTMLElement, model: ElecModel, key: string): void {
  const w = model.wires.find((x) => x.key === key);
  if (!w) return;
  const name = (k: string): string => model.boxByKey.get(k)?.lines[0] ?? k;
  const set = (figures: ElecWireFigures, opts?: { push?: boolean; panel?: boolean }): void => { elecEdit({ kind: 'wire', key, figures }, opts); };
  body.appendChild(sectionTitle('Schematic wire'));
  body.appendChild(infoRow('No.', w.number));
  body.appendChild(infoRow('From', name(w.from)));
  body.appendChild(infoRow('To', name(w.to)));
  if (w.kind === 'hand') {
    body.appendChild(selectRow('Installed by', [['stenal', 'Stenal'], ['client', 'Client']], w.installedBy, (v) => set({ installedBy: v === 'client' ? 'client' : 'stenal' })));
    body.appendChild(textFieldRow('Cable', w.cable, '', (v) => set({ cable: v }, { panel: false })));
    body.appendChild(numberRow('Length (mm)', w.lengthTyped ?? null, (v) => set({ length: v }, { panel: false }), { commit: true, placeholder: '—', onClear: () => set({ length: null }, { panel: false }) }));
  } else {
    body.appendChild(infoRow('Installed by', tr(w.installedBy === 'client' ? 'Client' : 'Stenal')));
    if (w.cable) body.appendChild(infoRow('Cable', w.cable));
    body.appendChild(infoRow('Length', w.length != null ? formatCableLength(w.length) : '—'));
  }
  // Every wire's figures in one dialog: a hand wire's ends are only there.
  body.appendChild(buttonRow('Edit wire…', () => { void elecEditWire(w.key); }));
  if (w.kind === 'auto') body.appendChild(selectRow('Fed from', elecFeedOptions(model, w), w.fed ?? '', (v) => set({ fed: v || null })));
  body.appendChild(checkboxRow('Straight line', !!w.straight, (v) => set({ straight: v })));
  if (w.jog != null) body.appendChild(buttonRow('Reset the jog', () => set({ jog: null })));
  // How it is drawn, as a sheet connection's card has it: its own line width,
  // dash and colour over the schematic's and its installer's. colorRow records
  // its own step.
  body.appendChild(numberRow('Line width (mm)', w.look.thickness, (v) => set({ thickness: v }), { commit: true }));
  body.appendChild(lineStyleRow(w.look.style, (v) => set({ style: v })));
  body.appendChild(colorRow('Colour', w.look.color, (hex) => set({ color: hex }, { push: false })));
  if (w.color) body.appendChild(buttonRow('Use installer colour', () => set({ color: null })));
  if (w.thickness != null || w.style) body.appendChild(buttonRow('Use the schematic’s width and style', () => set({ thickness: null, style: null })));
}

/** Where an automatic wire can be fed from: automatically, a junction box (not
 *  its own), or the box it is fed from now, whatever that is (a feed's end
 *  dragged onto any box). */
function elecFeedOptions(model: ElecModel, w: ElecWire): Array<readonly [string, string]> {
  const boxes = model.boxes.filter((b) => (b.kind === 'product' && b.junction && b.key !== w.from) || b.key === w.fed);
  return [['', 'Automatic'], ...boxes.map((b) => [b.key, b.lines[0] ?? b.key] as const)];
}

function renderElecCellPanel(body: HTMLElement, model: ElecModel, cell: ElecCellId): void {
  body.appendChild(sectionTitle('Schematic cell'));
  body.appendChild(infoRow('Cell', elecCellName(model, cell)));
  const { row, key } = parseElecCell(cell);
  if (row === 'technical') {
    const pr = model.plantRooms.find((p) => p.id === key);
    if (pr) body.appendChild(nameRow(pr.entered, dictDefaults(elecPlantRoomWord(pr), trIn), (v) => elecEdit({ kind: 'plantRoom', id: key, name: v }), { commit: true }));
  }
  body.appendChild(buttonRow('Add a box here', () => elecAddHandBox(cell)));
}

function renderElecBorderPanel(body: HTMLElement, model: ElecModel, row: ElecRowId, after: string): void {
  const b = model.borders.find((x) => x.row === row && x.after === after);
  body.appendChild(sectionTitle('Cell border'));
  body.appendChild(numberRow('Offset (mm)', b?.dx ?? 0, (v) => elecEdit({ kind: 'border', row, after, dx: v }, { panel: false }), { commit: true }));
  if (b) body.appendChild(buttonRow('Reset border', () => elecEdit({ kind: 'border', row, after, dx: null, removed: false })));
}

function renderElecColumnPanel(body: HTMLElement, model: ElecModel, id: string): void {
  const col = model.columns.find((c) => c.id === id);
  if (!col) return;
  body.appendChild(sectionTitle('Schematic column'));
  body.appendChild(nameRow(col.entered, elecColumnDefaults(col), (v) => elecEdit({ kind: 'column', id, name: v }), { commit: true }));
  body.appendChild(colorRow('Colour', col.color, (hex) => elecEdit({ kind: 'column', id, color: hex }, { push: false, panel: false })));
  body.appendChild(buttonRow('Hide column', () => { elecEdit({ kind: 'column', id, hidden: true }, { panel: false }); elecSheet.elecSub = null; renderProperties(); }));
}

function renderElecRowPanel(body: HTMLElement, model: ElecModel, layout: ElecLayout, id: ElecRowId): void {
  const row = model.rows.find((r) => r.id === id);
  const rect = layout.rows.find((r) => r.id === id)?.rect;
  if (!row) return;
  body.appendChild(sectionTitle('Schematic row'));
  body.appendChild(infoRow('Row', row.name));
  if (rect) body.appendChild(numberRow('Height (mm)', Math.round(rect.h * 10) / 10, (v) => elecEdit({ kind: 'rowHeight', row: id, h: v }, { panel: false }), { commit: true }));
  if (model.rowHeights[id] != null) body.appendChild(buttonRow('Automatic height', () => elecEdit({ kind: 'rowHeight', row: id, h: null })));
}

function renderElecAsidePanel(body: HTMLElement, view: ElecView, which: 'table' | 'legend'): void {
  body.appendChild(sectionTitle(which === 'table' ? 'Wire table' : 'Legend'));
  body.appendChild(buttonRow(which === 'table' ? 'Hide the table' : 'Hide the legend', () => { elecEdit(elecAsideShown(view, which, false), { panel: false }); elecSheet.elecSub = null; renderProperties(); }));
  if (view[which]) body.appendChild(buttonRow('Back beside the schematic', () => elecEdit({ kind: 'viewAside', view, which, at: null })));
}

/** The view's columns: in it or not, and the schematic's own — name, colour,
 *  order, hidden, a hand column added or removed (decision 2). */
function renderElecColumnsSection(body: HTMLElement, sh: Sheet, view: ElecView, model: ElecModel): void {
  body.appendChild(sectionTitle('Columns'));
  const inView = new Set(elecNow.columns(view));
  const visible = model.columns.map((c) => c.id);
  for (const c of [...model.columns, ...model.hiddenColumns]) {
    const hidden = model.hiddenColumns.includes(c);
    const card = document.createElement('div'); card.className = 'conn-card elec-col'; card.dataset.col = c.id;
    const head = document.createElement('div'); head.className = 'conn-head';
    const check = document.createElement('input'); check.type = 'checkbox'; check.checked = !hidden && inView.has(c.id); check.disabled = hidden;
    check.title = tr('Shown in this view');
    check.addEventListener('change', () => {
      // One step: what a view shows by the rule is written where it is first.
      pushHistory();
      elecSettleViewColumns(elecModelNow(), state.drawing);
      elecEdit({ kind: 'viewColumn', view, id: c.id, shown: check.checked }, { push: false });
    });
    const defaults = elecColumnDefaults(c);
    const name = document.createElement('input'); name.type = 'text'; name.value = c.entered ?? ''; name.style.flex = '1';
    name.placeholder = defaultName(defaults, getUiLang());
    name.addEventListener('change', () => elecEdit({ kind: 'column', id: c.id, name: name.value }));
    const colour = document.createElement('input'); colour.type = 'color'; colour.value = c.color; colour.title = tr('Colour');
    colour.addEventListener('change', () => elecEdit({ kind: 'column', id: c.id, color: colour.value }, { panel: false }));
    const btn = (text: string, title: string, run: () => void, enabled = true): HTMLButtonElement => {
      const b = document.createElement('button'); b.className = 'mini'; b.textContent = text; b.title = tr(title); b.disabled = !enabled;
      b.addEventListener('click', run);
      return b;
    };
    const i = visible.indexOf(c.id);
    head.append(check, name, colour,
      btn('↑', 'Move up', () => elecEdit({ kind: 'columnMove', id: c.id, by: -1 }), !hidden && i > 0),
      btn('↓', 'Move down', () => elecEdit({ kind: 'columnMove', id: c.id, by: 1 }), !hidden && i < visible.length - 1),
      btn(hidden ? '◌' : '●', hidden ? 'Show column' : 'Hide column', () => elecEdit({ kind: 'column', id: c.id, hidden: !hidden })));
    if (c.hand) head.append(btn('✕', 'Remove column', () => elecEdit({ kind: 'columnRemove', id: c.id, view })));
    card.append(head, nameDefaultsRows(defaults));
    body.appendChild(card);
  }
  body.appendChild(buttonRow('Add a column', () => elecEdit({ kind: 'columnAdd', id: schUid('col'), view })));
  body.appendChild(buttonRow('Split to a new sheet', () => splitElecView(sh, view)));
}

/** The Technical rooms band's plant rooms: renamed, reordered, added and removed
 *  (decisions 3 / 21). */
function renderElecPlantRoomsSection(body: HTMLElement, model: ElecModel): void {
  body.appendChild(sectionTitle('Plant rooms'));
  const list = model.plantRooms;
  list.forEach((p, i) => {
    const card = document.createElement('div'); card.className = 'conn-card elec-plant';
    const head = document.createElement('div'); head.className = 'conn-head';
    const defaults = dictDefaults(elecPlantRoomWord(p), trIn);
    const name = document.createElement('input'); name.type = 'text'; name.value = p.entered ?? ''; name.style.flex = '1';
    name.placeholder = defaultName(defaults, getUiLang());
    name.addEventListener('change', () => elecEdit({ kind: 'plantRoom', id: p.id, name: name.value }));
    const btn = (text: string, title: string, run: () => void, enabled: boolean): HTMLButtonElement => {
      const b = document.createElement('button'); b.className = 'mini'; b.textContent = text; b.title = tr(title); b.disabled = !enabled;
      b.addEventListener('click', run);
      return b;
    };
    head.append(name,
      btn('↑', 'Move up', () => elecEdit({ kind: 'plantRoomMove', id: p.id, by: -1 }), i > 0),
      btn('↓', 'Move down', () => elecEdit({ kind: 'plantRoomMove', id: p.id, by: 1 }), i < list.length - 1),
      btn('✕', 'Remove plant room', () => elecEdit({ kind: 'plantRoomRemove', id: p.id }), list.length > 1));
    card.append(head, nameDefaultsRows(defaults));
    body.appendChild(card);
  });
  body.appendChild(buttonRow('Add a plant room', () => elecEdit({ kind: 'plantRoomAdd', id: schUid('plant') })));
}

/** The fixed rows that are empty can be hidden (decision 3). */
function renderElecRowsSection(body: HTMLElement, model: ElecModel): void {
  const empty = model.rows.filter((r) => r.empty);
  if (!empty.length) return;
  body.appendChild(sectionTitle('Empty rows'));
  for (const r of empty) {
    const row = document.createElement('div'); row.className = 'prop-row';
    const lab = document.createElement('label'); lab.textContent = r.name;
    const b = document.createElement('button');
    b.textContent = r.hidden ? tr('Hidden') : tr('Shown');
    b.className = r.hidden ? 'toggle' : 'toggle active';
    b.addEventListener('click', () => elecEdit({ kind: 'rowHidden', row: r.id, hidden: !r.hidden }));
    row.append(lab, b);
    body.appendChild(row);
  }
}

/** What was hidden or removed by hand, each to bring back. */
function renderElecHiddenSection(body: HTMLElement, model: ElecModel): void {
  const borders = model.borders.filter((b) => b.removed);
  if (!model.hiddenBoxes.length && !model.removedWires.length && !borders.length) return;
  body.appendChild(sectionTitle('Hidden and removed'));
  const item = (text: string, label: string, edit: ElecEdit): void => {
    const row = document.createElement('div'); row.className = 'prop-row';
    const lab = document.createElement('label'); lab.textContent = text;
    const b = document.createElement('button'); b.textContent = tr(label);
    b.addEventListener('click', () => elecEdit(edit));
    row.append(lab, b);
    body.appendChild(row);
  };
  const boxName = (k: string): string => (model.boxByKey.get(k) ?? model.hiddenBoxes.find((b) => b.key === k))?.lines[0] ?? '';
  for (const b of model.hiddenBoxes) item(b.lines[0] ?? '', 'Show', { kind: 'boxHidden', key: b.key, hidden: false });
  for (const w of model.removedWires) item([w.number, `${boxName(w.from)} – ${boxName(w.to)}`].filter(Boolean).join(' · '), 'Restore', { kind: 'wireRemoved', key: w.key, removed: false });
  const colName = (id: string): string => model.columns.find((c) => c.id === id)?.name ?? model.plantRooms.find((p) => p.id === id)?.name ?? id;
  for (const b of borders) item(`${colName(b.after)} | ${model.rows.find((r) => r.id === b.row)?.name ?? b.row}`, 'Restore', { kind: 'border', row: b.row, after: b.after, removed: false });
}
